// src/pages/FamilyDetail.jsx - Vue détaillée avec gestion multi-clients
import ValuationCaption from "../components/ValuationCaption";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "../lib/supabaseClient";
import { useParams, useNavigate } from "react-router-dom";
import { generateFamilyPdf } from "../utils/FamilyDetailPdfExport";
import { useTranslation } from "react-i18next";
import { 
   ChevronLeft, 
   Users, 
   FileText, 
   Shield, 
   Heart,
   TrendingUp,
   Package,
   Edit,
   Trash2,
   AlertCircle,
   UserPlus,
   Crown,
   Mail,
   Building2,
   X,
   Briefcase,
   Landmark,
   HandHeart,
   Banknote,
   Download
} from "lucide-react";

const COPY = {
  fr: {
    noProfile: "Aucun profil trouvé avec cet email",
    loadingFam: "Chargement de la famille...",
    backDash: "Retour au dashboard",
    backDashArrow: "← Retour au dashboard",
    genReport: "Générer un rapport",
    addClient: "Ajouter un client",
    titleEdit: "Modifier",
    titleDelete: "Supprimer",
    totalValue: "Valeur totale",
    emailClient: "Email du client",
    role: "Rôle",
    ownerMain: "Propriétaire principal",
    familyMember: "Membre de la famille",
    cancel: "Annuler",
    overview: "Vue d'ensemble",
    familyStructure: "Structure familiale",
    memberClients: "Clients membres",
    beneficiaries: "Bénéficiaires",
    artHoldings: "Patrimoine artistique",
    works: "Œuvres",
    value: "Valeur",
    insurance: "Assurance",
    notConfigured: "Non configurée",
    succPlan: "Plan de succession",
    noClients: "Aucun client dans cette famille",
    noCollections: "Aucune collection",
    noBenef: "Aucun bénéficiaire défini",
    primaryContact: "Contact principal",
    setPrimary: "Définir comme contact principal",
    removeFromFam: "Retirer de la famille",
    fiscalTitle: "Régime fiscal de référence",
    fiscalIntro: "Définissez le régime fiscal par défaut pour cette famille. Ce paramètre sera utilisé dans les exports de rapports (ventes, donations, succession) pour calculer les implications fiscales.",
    fiscalApplied: "Ce régime sera appliqué par défaut dans tous les documents générés pour cette famille (rapports de vente, plans de succession, donations). Vous pourrez toujours le modifier pour un document spécifique si nécessaire.",
    fiscalNote: "Les modifications seront enregistrées pour cette famille uniquement",
    save: "Enregistrer",
    philNoClient: "Aucun client n’est rattaché à cette famille.",
    philSynth: "Synthèse actuelle du patrimoine engagé",
    exportPdfTitle: "Exporter le plan en PDF",
    levSynth: "Synthèse des leviers",
    dismember: "Démembrement de propriété : usufruit & nue-propriété",
    usufructuary: "👑 Usufruitier",
    u1: "Droit d’utiliser l’œuvre et d’en percevoir les revenus (prêt, exposition, location).",
    u2: "Conserve la jouissance de l’œuvre pendant toute la durée fixée.",
    u3: "Ne peut pas vendre l’œuvre ni en disposer définitivement.",
    uPractice: "En pratique : l’usufruitier peut exposer l’œuvre, la prêter à un musée ou l’assurer à son nom, mais ne peut pas la vendre.",
    bareOwner: "💎 Nu-propriétaire",
    b1: "Détient la valeur patrimoniale de l’œuvre.",
    b2: "Ne peut ni l’utiliser ni la louer pendant l’usufruit.",
    b3: "Récupère la pleine propriété à l’extinction de l’usufruit.",
    bPractice: "En pratique : le nu-propriétaire ne peut pas exposer ni prêter l’œuvre tant que l’usufruit est en cours.",
    famNote: "Note du Family Office",
    famNotePh: "Points d’attention, institutions cibles, étapes prochaines…",
    exportPdfBtn: "Exporter le PDF",
    rg1l: "France - Plus-value sur cession",
    rg1d: "Taxation à 36,2% (19% IR + 17,2% PS) sur la plus-value. Abattement pour durée de détention : 5% par an de la 3e à 22e année.",
    rg1o: "Idéal pour collections à long terme, transmission progressive",
    rg2l: "France - Droits de succession",
    rg2d: "Barème progressif 5-45% selon lien de parenté et montant. Abattement 100k€ ligne directe. Œuvres d'art exonérées si dation.",
    rg2o: "Important pour planification successorale",
    rg3l: "France - Exonération ISF/IFI",
    rg3d: "Œuvres d'art, antiquités et objets de collection totalement exonérés d'IFI.",
    rg3o: "Avantage patrimonial significatif",
    rg4l: "France - Mécénat",
    rg4d: "Réduction d'impôt 66% des dons (plafond 20% revenu). Donation avec usufruit : déduction immédiate + 0% succession.",
    rg4o: "Optimisation via donations institutions",
    rg5l: "Belgique - Plus-value",
    rg5d: "Vente entre particuliers : 0% si non spéculatif. Vente professionnelle : 33% (sociétés).",
    rg5o: "Favorable pour ventes occasionnelles",
    rg6l: "Suisse - Plus-value",
    rg6d: "Gain privé non imposable. Professionnel : impôt cantonal + fédéral (~20-25%).",
    rg6o: "Très favorable pour particuliers",
    rg7d: "20% sur plus-value (28% si résidence). Exemption 6k£/an. Chattels exemption 6k£.",
    rg7o: "Favorable pour œuvres < 6k£",
    rg8l: "Autre juridiction",
    rg8d: "Régime fiscal spécifique à définir avec conseiller fiscal.",
    rg8o: "Consultation expert requise",
    tabOverview: "Vue d'ensemble",
    tabPhil: "Mécénat",
    tabFiscal: "Préférences fiscales",
    repOverview: "Rapport patrimonial",
    repSucc: "Rapport successoral",
    repPhil: "Stratégie de mécénat",
    alertEmail: "Veuillez entrer un email",
    alertAlready: "Cette personne est déjà cliente de cette famille",
    alertSavedFiscal: "Régime fiscal sauvegardé",
    alertSaveErr: "Erreur lors de la sauvegarde",
    alertNoRef: "Aucun client de référence.",
    alertPlanNoHist: "Plan créé (non historisé — table indisponible).",
    alertPlanHist: "Plan créé et historisé.",
    alertPlanNoHist2: "Plan créé (non historisé).",
    famNotFound: "Famille introuvable",
    adding: "Ajout...",
    add: "Ajouter",
    noName: "Sans nom",
    unknownProfile: "Profil inconnu",
    unknownClient: "Client inconnu",
    toDefine: "À définir",
    saving: "Enregistrement…",
    createPlan: "Créer le plan",
    eligible: "Éligible",
    notApplicable: "Non applicable",
    lev1: "Prêt d’œuvres à des institutions",
    lev1a: "Dépôt temporaire ou convention de longue durée",
    lev1b: "Frais de conservation/assurance/restauration potentiellement déductibles (à confirmer)",
    lev1c: "Frais potentiellement déductibles selon pays",
    lev1d: "Visibilité et légitimation de la collection",
    lev2: "Dation en paiement (FR)",
    lev2a: "Règlement des droits de succession en œuvres",
    lev2b: "Évaluation à la valeur de marché (pas de plus-value taxée)",
    lev2c: "Œuvres exceptionnelles acceptées par musées nationaux",
    lev3: "Fondation d’entreprise ou sous égide",
    lev3a: "Structure pérenne pour gérer la collection",
    lev3b: "Déduction fiscale des apports (selon juridiction)",
    lev3c: "Organisation d’expositions et prêts muséaux",
    lev4: "Don avec usufruit / nue-propriété (FR)",
    lev4a: "Conservation de la jouissance de l’œuvre",
    lev4b: "Réduction fiscale immédiate sur la valeur de la NP",
    lev4c: "Transmission progressive sans fiscalité successorale",
    pr1: "FR — Forfait 6,5% (>5k€)",
    pr2: "FR — PV 19% + 17,2% (abattement)",
    pr3: "UK — CGT ~20%",
    pr4: "DE — Exonération > 1 an",
    pr5: "Non sélectionné",
    createdOn: "Créée le",
    currentRegime: "Régime actuellement sélectionné :",
    errorPrefix: "Erreur : ",
    errorDelete: "Erreur lors de la suppression : ",
    roleOwner: "Propriétaire",
    roleMember: "Membre",
    from: "De :",
    refClient: "Client de référence :",
    countryLbl: "Pays :",
    regimeLbl: "Régime :",
    philTitle: "Mécénat & philanthropie",
    exceptionalWorks: "Œuvres exceptionnelles :",
    usufructWorks: "Œuvres en usufruit :",
    bareWorks: "Œuvres en nue-propriété :",
    taxGain: "Estimation gain fiscal (indicatif) :",
    exportPdf: "Export PDF",
    noteB: "À noter :",
    noteTxt: "dans le module Family Office, ces informations peuvent être synchronisées avec le profil du client afin d’indiquer qui détient l’usage ou la nue-propriété de chaque œuvre. Cela permettra de déterminer automatiquement les droits d’exposition, de prêt ou de location.",
    disclB: "Disclaimer.",
    disclTxt: "Estimations indicatives basées sur les informations disponibles dans ARTICHAUT. Vérifier les règles applicables avec un conseil fiscal / avocat ; les seuils et mécanismes varient selon la juridiction.",
    nClients: (n) => `${n} client${n > 1 ? "s" : ""}`,
    nBenef: (n) => `${n} bénéficiaire${n > 1 ? "s" : ""}`,
    nBenefParen: (n) => `${n} bénéficiaire(s)`,
    nWorks: (n) => `${n} œuvres`,
    nArtworks: (n) => `${n} œuvre${n > 1 ? "s" : ""}`,
    deleteConfirm: (label) => `Supprimer la famille "${label}" ?\n\n⚠️ Cette action supprimera uniquement la famille.\nLes clients et leurs collections resteront intacts.`,
  },
  en: {
    noProfile: "No profile found with this email",
    loadingFam: "Loading the family...",
    backDash: "Back to the dashboard",
    backDashArrow: "← Back to the dashboard",
    genReport: "Generate a report",
    addClient: "Add a client",
    titleEdit: "Edit",
    titleDelete: "Delete",
    totalValue: "Total value",
    emailClient: "Client's email",
    role: "Role",
    ownerMain: "Main owner",
    familyMember: "Family member",
    cancel: "Cancel",
    overview: "Overview",
    familyStructure: "Family structure",
    memberClients: "Member clients",
    beneficiaries: "Beneficiaries",
    artHoldings: "Art holdings",
    works: "Artworks",
    value: "Value",
    insurance: "Insurance",
    notConfigured: "Not configured",
    succPlan: "Succession plan",
    noClients: "No client in this family",
    noCollections: "No collection",
    noBenef: "No beneficiary defined",
    primaryContact: "Primary contact",
    setPrimary: "Set as primary contact",
    removeFromFam: "Remove from the family",
    fiscalTitle: "Reference tax regime",
    fiscalIntro: "Define the default tax regime for this family. This setting will be used in report exports (sales, donations, succession) to calculate the tax implications.",
    fiscalApplied: "This regime will be applied by default in all documents generated for this family (sales reports, succession plans, donations). You can still change it for a specific document if needed.",
    fiscalNote: "Changes will be saved for this family only",
    save: "Save",
    philNoClient: "No client is attached to this family.",
    philSynth: "Current summary of the committed holdings",
    exportPdfTitle: "Export the plan as a PDF",
    levSynth: "Summary of levers",
    dismember: "Split ownership: usufruct & bare ownership",
    usufructuary: "👑 Usufructuary",
    u1: "Right to use the artwork and collect its income (loan, exhibition, rental).",
    u2: "Keeps the enjoyment of the artwork for the whole agreed period.",
    u3: "Cannot sell the artwork or dispose of it permanently.",
    uPractice: "In practice: the usufructuary can exhibit the artwork, lend it to a museum or insure it in their name, but cannot sell it.",
    bareOwner: "💎 Bare owner",
    b1: "Holds the capital value of the artwork.",
    b2: "Can neither use nor rent it during the usufruct.",
    b3: "Recovers full ownership when the usufruct ends.",
    bPractice: "In practice: the bare owner can neither exhibit nor lend the artwork while the usufruct is ongoing.",
    famNote: "Family Office note",
    famNotePh: "Points of attention, target institutions, next steps…",
    exportPdfBtn: "Export the PDF",
    rg1l: "France - Capital gains on sale",
    rg1d: "Taxed at 36.2% (19% income tax + 17.2% social charges) on the gain. Holding-period allowance: 5% per year from the 3rd to the 22nd year.",
    rg1o: "Ideal for long-term collections and gradual transmission",
    rg2l: "France - Inheritance duties",
    rg2d: "Progressive scale 5-45% depending on family relationship and amount. €100k allowance in direct line. Artworks exempt if given in lieu of payment (dation).",
    rg2o: "Important for succession planning",
    rg3l: "France - ISF/IFI exemption",
    rg3d: "Artworks, antiques and collectors' items are fully exempt from IFI (real-estate wealth tax).",
    rg3o: "Significant wealth advantage",
    rg4l: "France - Patronage",
    rg4d: "Tax reduction of 66% of donations (cap 20% of income). Donation with usufruct: immediate deduction + 0% inheritance tax.",
    rg4o: "Optimisation through donations to institutions",
    rg5l: "Belgium - Capital gains",
    rg5d: "Sale between private individuals: 0% if non-speculative. Professional sale: 33% (companies).",
    rg5o: "Favourable for occasional sales",
    rg6l: "Switzerland - Capital gains",
    rg6d: "Private gain not taxable. Professional: cantonal + federal tax (~20-25%).",
    rg6o: "Very favourable for private individuals",
    rg7d: "20% on gains (28% on residential property). £6k annual exemption. Chattels exemption £6k.",
    rg7o: "Favourable for artworks under £6k",
    rg8l: "Other jurisdiction",
    rg8d: "Specific tax regime to be defined with a tax adviser.",
    rg8o: "Expert consultation required",
    tabOverview: "Overview",
    tabPhil: "Patronage",
    tabFiscal: "Tax preferences",
    repOverview: "Wealth report",
    repSucc: "Succession report",
    repPhil: "Patronage strategy",
    alertEmail: "Please enter an email",
    alertAlready: "This person is already a client of this family",
    alertSavedFiscal: "Tax regime saved",
    alertSaveErr: "Error while saving",
    alertNoRef: "No reference client.",
    alertPlanNoHist: "Plan created (not recorded — table unavailable).",
    alertPlanHist: "Plan created and recorded.",
    alertPlanNoHist2: "Plan created (not recorded).",
    famNotFound: "Family not found",
    adding: "Adding...",
    add: "Add",
    noName: "No name",
    unknownProfile: "Unknown profile",
    unknownClient: "Unknown client",
    toDefine: "To be defined",
    saving: "Saving…",
    createPlan: "Create the plan",
    eligible: "Eligible",
    notApplicable: "Not applicable",
    lev1: "Loan of artworks to institutions",
    lev1a: "Temporary deposit or long-term agreement",
    lev1b: "Conservation/insurance/restoration costs potentially deductible (to be confirmed)",
    lev1c: "Costs potentially deductible depending on the country",
    lev1d: "Visibility and legitimation of the collection",
    lev2: "Dation in payment (FR)",
    lev2a: "Payment of inheritance duties in artworks",
    lev2b: "Valued at market value (no taxed capital gain)",
    lev2c: "Exceptional artworks accepted by national museums",
    lev3: "Corporate or sheltered foundation",
    lev3a: "Lasting structure to manage the collection",
    lev3b: "Tax deduction of contributions (depending on jurisdiction)",
    lev3c: "Organisation of exhibitions and museum loans",
    lev4: "Gift with usufruct / bare ownership (FR)",
    lev4a: "Keeping the enjoyment of the artwork",
    lev4b: "Immediate tax reduction on the value of the bare ownership",
    lev4c: "Gradual transmission without inheritance tax",
    pr1: "FR — Flat 6.5% (>€5k)",
    pr2: "FR — Capital gains 19% + 17.2% (allowance)",
    pr3: "UK — CGT ~20%",
    pr4: "DE — Exempt after 1 year",
    pr5: "Not selected",
    createdOn: "Created on",
    currentRegime: "Currently selected regime:",
    errorPrefix: "Error: ",
    errorDelete: "Error while deleting: ",
    roleOwner: "Owner",
    roleMember: "Member",
    from: "From:",
    refClient: "Reference client:",
    countryLbl: "Country:",
    regimeLbl: "Regime:",
    philTitle: "Patronage & philanthropy",
    exceptionalWorks: "Exceptional artworks:",
    usufructWorks: "Artworks under usufruct:",
    bareWorks: "Artworks in bare ownership:",
    taxGain: "Estimated tax gain (indicative):",
    exportPdf: "Export PDF",
    noteB: "Note:",
    noteTxt: "in the Family Office module, this information can be synchronised with the client's profile to show who holds the use or the bare ownership of each artwork. This will make it possible to determine automatically the rights to exhibit, lend or rent.",
    disclB: "Disclaimer.",
    disclTxt: "Indicative estimates based on the information available in ARTICHAUT. Check the applicable rules with a tax adviser / lawyer; thresholds and mechanisms vary by jurisdiction.",
    nClients: (n) => `${n} client${n === 1 ? "" : "s"}`,
    nBenef: (n) => `${n} beneficiar${n === 1 ? "y" : "ies"}`,
    nBenefParen: (n) => `${n} beneficiary(ies)`,
    nWorks: (n) => `${n} artworks`,
    nArtworks: (n) => `${n} artwork${n === 1 ? "" : "s"}`,
    deleteConfirm: (label) => `Delete the family "${label}"?\n\n⚠️ This action deletes the family only.\nThe clients and their collections stay intact.`,
  },
  de: {
    noProfile: "Kein Profil mit dieser E-Mail gefunden",
    loadingFam: "Familie wird geladen...",
    backDash: "Zurück zum Dashboard",
    backDashArrow: "← Zurück zum Dashboard",
    genReport: "Bericht erstellen",
    addClient: "Kunden hinzufügen",
    titleEdit: "Bearbeiten",
    titleDelete: "Löschen",
    totalValue: "Gesamtwert",
    emailClient: "E-Mail des Kunden",
    role: "Rolle",
    ownerMain: "Hauptinhaber",
    familyMember: "Familienmitglied",
    cancel: "Abbrechen",
    overview: "Überblick",
    familyStructure: "Familienstruktur",
    memberClients: "Mitglieds-Kunden",
    beneficiaries: "Begünstigte",
    artHoldings: "Kunstbesitz",
    works: "Werke",
    value: "Wert",
    insurance: "Versicherung",
    notConfigured: "Nicht eingerichtet",
    succPlan: "Nachfolgeplan",
    noClients: "Kein Kunde in dieser Familie",
    noCollections: "Keine Sammlung",
    noBenef: "Kein Begünstigter definiert",
    primaryContact: "Hauptkontakt",
    setPrimary: "Als Hauptkontakt festlegen",
    removeFromFam: "Aus der Familie entfernen",
    fiscalTitle: "Steuerliches Referenzregime",
    fiscalIntro: "Legen Sie das Standard-Steuerregime für diese Familie fest. Diese Einstellung wird in Berichtsexporten (Verkäufe, Schenkungen, Nachfolge) zur Berechnung der steuerlichen Folgen verwendet.",
    fiscalApplied: "Dieses Regime wird standardmäßig in allen für diese Familie erstellten Dokumenten angewendet (Verkaufsberichte, Nachfolgepläne, Schenkungen). Sie können es bei Bedarf für ein bestimmtes Dokument ändern.",
    fiscalNote: "Änderungen werden nur für diese Familie gespeichert",
    save: "Speichern",
    philNoClient: "Dieser Familie ist kein Kunde zugeordnet.",
    philSynth: "Aktuelle Zusammenfassung des gebundenen Besitzes",
    exportPdfTitle: "Plan als PDF exportieren",
    levSynth: "Zusammenfassung der Hebel",
    dismember: "Eigentumsaufteilung: Nießbrauch & nacktes Eigentum",
    usufructuary: "👑 Nießbraucher",
    u1: "Recht, das Werk zu nutzen und seine Erträge zu beziehen (Leihgabe, Ausstellung, Vermietung).",
    u2: "Behält die Nutzung des Werks für die gesamte vereinbarte Dauer.",
    u3: "Darf das Werk weder verkaufen noch endgültig darüber verfügen.",
    uPractice: "In der Praxis: Der Nießbraucher kann das Werk ausstellen, einem Museum leihen oder auf seinen Namen versichern, aber nicht verkaufen.",
    bareOwner: "💎 Nackter Eigentümer",
    b1: "Hält den Vermögenswert des Werks.",
    b2: "Darf es während des Nießbrauchs weder nutzen noch vermieten.",
    b3: "Erhält das Volleigentum zurück, wenn der Nießbrauch endet.",
    bPractice: "In der Praxis: Der nackte Eigentümer kann das Werk weder ausstellen noch verleihen, solange der Nießbrauch läuft.",
    famNote: "Notiz des Family Office",
    famNotePh: "Aufmerksamkeitspunkte, Zielinstitutionen, nächste Schritte…",
    exportPdfBtn: "PDF exportieren",
    rg1l: "Frankreich - Veräußerungsgewinn",
    rg1d: "Besteuerung mit 36,2 % (19 % ESt + 17,2 % Sozialabgaben) auf den Gewinn. Haltefristabschlag: 5 % pro Jahr vom 3. bis zum 22. Jahr.",
    rg1o: "Ideal für langfristige Sammlungen und schrittweise Übertragung",
    rg2l: "Frankreich - Erbschaftsteuer",
    rg2d: "Progressiver Tarif 5-45 % je nach Verwandtschaftsgrad und Betrag. Freibetrag 100.000 € in gerader Linie. Kunstwerke befreit bei Hingabe an Zahlungs statt (Dation).",
    rg2o: "Wichtig für die Nachfolgeplanung",
    rg3l: "Frankreich - Befreiung von ISF/IFI",
    rg3d: "Kunstwerke, Antiquitäten und Sammlerobjekte sind vollständig von der IFI (Immobilienvermögensteuer) befreit.",
    rg3o: "Erheblicher Vermögensvorteil",
    rg4l: "Frankreich - Mäzenatentum",
    rg4d: "Steuerermäßigung von 66 % der Spenden (Obergrenze 20 % des Einkommens). Schenkung mit Nießbrauch: sofortiger Abzug + 0 % Erbschaftsteuer.",
    rg4o: "Optimierung über Schenkungen an Institutionen",
    rg5l: "Belgien - Veräußerungsgewinn",
    rg5d: "Verkauf zwischen Privatpersonen: 0 % wenn nicht spekulativ. Gewerblicher Verkauf: 33 % (Gesellschaften).",
    rg5o: "Günstig für gelegentliche Verkäufe",
    rg6l: "Schweiz - Veräußerungsgewinn",
    rg6d: "Privater Gewinn steuerfrei. Gewerblich: kantonale + Bundessteuer (~20-25 %).",
    rg6o: "Sehr günstig für Privatpersonen",
    rg7d: "20 % auf Gewinne (28 % bei Wohnimmobilien). Freibetrag 6.000 £/Jahr. Chattels-Freibetrag 6.000 £.",
    rg7o: "Günstig für Werke unter 6.000 £",
    rg8l: "Andere Rechtsordnung",
    rg8d: "Spezielles Steuerregime, mit einem Steuerberater festzulegen.",
    rg8o: "Expertenberatung erforderlich",
    tabOverview: "Überblick",
    tabPhil: "Mäzenatentum",
    tabFiscal: "Steuerpräferenzen",
    repOverview: "Vermögensbericht",
    repSucc: "Nachfolgebericht",
    repPhil: "Mäzenatentum-Strategie",
    alertEmail: "Bitte geben Sie eine E-Mail-Adresse ein",
    alertAlready: "Diese Person ist bereits Kunde dieser Familie",
    alertSavedFiscal: "Steuerregime gespeichert",
    alertSaveErr: "Fehler beim Speichern",
    alertNoRef: "Kein Referenzkunde.",
    alertPlanNoHist: "Plan erstellt (nicht protokolliert — Tabelle nicht verfügbar).",
    alertPlanHist: "Plan erstellt und protokolliert.",
    alertPlanNoHist2: "Plan erstellt (nicht protokolliert).",
    famNotFound: "Familie nicht gefunden",
    adding: "Wird hinzugefügt...",
    add: "Hinzufügen",
    noName: "Ohne Namen",
    unknownProfile: "Unbekanntes Profil",
    unknownClient: "Unbekannter Kunde",
    toDefine: "Festzulegen",
    saving: "Wird gespeichert…",
    createPlan: "Plan erstellen",
    eligible: "Berechtigt",
    notApplicable: "Nicht anwendbar",
    lev1: "Leihgabe von Werken an Institutionen",
    lev1a: "Befristete Hinterlegung oder langfristige Vereinbarung",
    lev1b: "Kosten für Konservierung/Versicherung/Restaurierung möglicherweise abzugsfähig (zu bestätigen)",
    lev1c: "Kosten möglicherweise abzugsfähig, je nach Land",
    lev1d: "Sichtbarkeit und Legitimation der Sammlung",
    lev2: "Dation an Zahlungs statt (FR)",
    lev2a: "Begleichung der Erbschaftsteuer in Werken",
    lev2b: "Bewertung zum Marktwert (kein besteuerter Veräußerungsgewinn)",
    lev2c: "Außergewöhnliche Werke werden von Nationalmuseen akzeptiert",
    lev3: "Unternehmensstiftung oder Treuhandstiftung",
    lev3a: "Dauerhafte Struktur zur Verwaltung der Sammlung",
    lev3b: "Steuerabzug der Einlagen (je nach Rechtsordnung)",
    lev3c: "Organisation von Ausstellungen und Museumsleihgaben",
    lev4: "Schenkung mit Nießbrauch / nacktem Eigentum (FR)",
    lev4a: "Behalt der Nutzung des Werks",
    lev4b: "Sofortige Steuerermäßigung auf den Wert des nackten Eigentums",
    lev4c: "Schrittweise Übertragung ohne Erbschaftsteuer",
    pr1: "FR — Pauschale 6,5 % (>5.000 €)",
    pr2: "FR — Veräußerungsgewinn 19 % + 17,2 % (Abschlag)",
    pr3: "UK — CGT ~20 %",
    pr4: "DE — Steuerfrei nach 1 Jahr",
    pr5: "Nicht ausgewählt",
    createdOn: "Erstellt am",
    currentRegime: "Aktuell gewähltes Regime:",
    errorPrefix: "Fehler: ",
    errorDelete: "Fehler beim Löschen: ",
    roleOwner: "Inhaber",
    roleMember: "Mitglied",
    from: "Von:",
    refClient: "Referenzkunde:",
    countryLbl: "Land:",
    regimeLbl: "Regime:",
    philTitle: "Mäzenatentum & Philanthropie",
    exceptionalWorks: "Außergewöhnliche Werke:",
    usufructWorks: "Werke unter Nießbrauch:",
    bareWorks: "Werke im nackten Eigentum:",
    taxGain: "Geschätzter Steuervorteil (Richtwert):",
    exportPdf: "PDF exportieren",
    noteB: "Hinweis:",
    noteTxt: "Im Family-Office-Modul können diese Informationen mit dem Kundenprofil synchronisiert werden, um anzuzeigen, wer die Nutzung oder das nackte Eigentum an jedem Werk hält. So lassen sich die Rechte zum Ausstellen, Verleihen oder Vermieten automatisch bestimmen.",
    disclB: "Haftungsausschluss.",
    disclTxt: "Richtwerte auf Grundlage der in ARTICHAUT verfügbaren Informationen. Prüfen Sie die geltenden Regeln mit einem Steuerberater / Anwalt; Schwellenwerte und Mechanismen variieren je nach Rechtsordnung.",
    nClients: (n) => `${n} ${n === 1 ? "Kunde" : "Kunden"}`,
    nBenef: (n) => `${n} ${n === 1 ? "Begünstigter" : "Begünstigte"}`,
    nBenefParen: (n) => `${n} Begünstigte(r)`,
    nWorks: (n) => `${n} Werke`,
    nArtworks: (n) => `${n} ${n === 1 ? "Werk" : "Werke"}`,
    deleteConfirm: (label) => `Familie "${label}" löschen?\n\n⚠️ Diese Aktion löscht nur die Familie.\nDie Kunden und ihre Sammlungen bleiben unverändert.`,
  },
};

function useCopy() {
  const { i18n } = useTranslation();
  const lg = String(i18n.resolvedLanguage || i18n.language || "fr").slice(0, 2);
  return {
    c: COPY[lg] || COPY.fr,
    locale: lg === "de" ? "de-DE" : lg === "en" ? "en-GB" : "fr-FR",
  };
}

function FiscalTab({ familyId, currentRegime, onSave }) {
  const { c } = useCopy();
  const [selectedRegime, setSelectedRegime] = useState(currentRegime || "FR_PLUS_VALUE");

  const regimes = [
    {
      id: "FR_PLUS_VALUE",
      label: c.rg1l,
      description: c.rg1d,
      optimal: c.rg1o
    },
    {
      id: "FR_SUCCESSION",
      label: c.rg2l,
      description: c.rg2d,
      optimal: c.rg2o
    },
    {
      id: "FR_ISF",
      label: c.rg3l,
      description: c.rg3d,
      optimal: c.rg3o
    },
    {
      id: "FR_MECENAT",
      label: c.rg4l,
      description: c.rg4d,
      optimal: c.rg4o
    },
    {
      id: "BE_PLUS_VALUE",
      label: c.rg5l,
      description: c.rg5d,
      optimal: c.rg5o
    },
    {
      id: "CH_PLUS_VALUE",
      label: c.rg6l,
      description: c.rg6d,
      optimal: c.rg6o
    },
    {
      id: "UK_CGT",
      label: "UK - Capital Gains Tax",
      description: c.rg7d,
      optimal: c.rg7o
    },
    {
      id: "OTHER",
      label: c.rg8l,
      description: c.rg8d,
      optimal: c.rg8o
    }
  ];

  const handleSave = () => {
    onSave(selectedRegime);
  };

  const currentRegimeDetails = regimes.find(r => r.id === selectedRegime);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-serif text-[#1A1A1A] mb-2">
          {c.fiscalTitle}
        </h3>
        <p className="text-sm text-[#666] font-light leading-relaxed">
          {c.fiscalIntro}
        </p>
      </div>

      {/* Sélection régime */}
      <div className="space-y-3">
        {regimes.map((regime) => (
          <div
            key={regime.id}
            onClick={() => setSelectedRegime(regime.id)}
            className={`border rounded-lg p-4 cursor-pointer transition-all ${
              selectedRegime === regime.id
                ? "border-[#1A1A1A] bg-[#FAFAFA]"
                : "border-[#E8E8E8] hover:border-[#999]"
            }`}
          >
            <div className="flex items-start gap-3">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                selectedRegime === regime.id
                  ? "border-[#1A1A1A]"
                  : "border-[#CCC]"
              }`}>
                {selectedRegime === regime.id && (
                  <div className="w-3 h-3 rounded-full bg-[#1A1A1A]"></div>
                )}
              </div>

              <div className="flex-1">
                <h4 className="font-serif text-[#1A1A1A] mb-1">{regime.label}</h4>
                <p className="text-sm text-[#666] font-light leading-relaxed mb-2">
                  {regime.description}
                </p>
                <p className="text-xs text-emerald-600 font-light">
                  ✓ {regime.optimal}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Aperçu régime sélectionné */}
      {currentRegimeDetails && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-serif text-[#1A1A1A] mb-2">
                {c.currentRegime} {currentRegimeDetails.label}
              </h4>
              <p className="text-sm text-[#666] font-light leading-relaxed">
                {c.fiscalApplied}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-6 border-t border-[#E8E8E8]">
        <p className="text-sm text-[#666] font-light">
          {c.fiscalNote}
        </p>
        <button
          onClick={handleSave}
          className="px-6 py-2.5 bg-[#1A1A1A] text-white rounded-lg hover:bg-[#333] transition-colors font-light"
        >
          {c.save}
        </button>
      </div>
    </div>
  );
}

export default function FamilyDetail() {
  const { c, locale } = useCopy();
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [family, setFamily] = useState(null);
  const [foProfile, setFoProfile] = useState(null);
  const [familyClients, setFamilyClients] = useState([]);
  const [collections, setCollections] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    // Ouvre automatiquement le bon onglet si ?rapport= est dans l'URL
    const rapportParam = new URLSearchParams(window.location.search).get("rapport");
    const validTabs = ["overview", "clients", "collections", "beneficiaries", "philanthropy", "fiscal"];
    const tabMap = { succession: "beneficiaries", philanthropy: "philanthropy", overview: "overview" };
    return tabMap[rapportParam] || (validTabs.includes(rapportParam) ? rapportParam : "overview");
  });
  const [fiscalRegime, setFiscalRegime] = useState(null); 
  
  // Modal d'ajout de client
  const [showAddClient, setShowAddClient] = useState(false);
  const [newClientEmail, setNewClientEmail] = useState("");
  const [newClientRole, setNewClientRole] = useState("member");
  const [addingClient, setAddingClient] = useState(false);

  const loadFamilyData = useCallback(async () => {
    setLoading(true);
    setError(null);
    // Collecteurs locaux pour triggerAutoExport (données fraîches avant setState)
    let clientsWithProfiles = [];
    let collectionsData = null;
    let artworksData = [];
    let beneficiariesData = null;

    try {
      // 1. Charger la famille
      const { data: familyData, error: familyError } = await supabase
        .from("families")
        .select("*")
        .eq("id", id)
        .maybeSingle();

      if (familyError) throw familyError;
      if (!familyData) throw new Error(c.famNotFound);

      setFamily(familyData);

      // Charger le profil du FO connecté (pour le branding PDF)
      let foProfileLocal = null;
      try {
        const { data: { user: foUser } } = await supabase.auth.getUser();
        if (foUser) {
          const { data: fo } = await supabase
            .from("profiles")
            .select("first_name, last_name, company")
            .eq("id", foUser.id)
            .single();
          if (fo) { setFoProfile(fo); foProfileLocal = fo; }
        }
      } catch (e) {
        console.warn("FO profile load skipped:", e?.message);
      }

// Charger régime fiscal
if (familyData.fiscal_regime) {
  setFiscalRegime(familyData.fiscal_regime);
}

      // 2. Charger TOUS les clients de la famille
      const { data: clientsData, error: clientsError } = await supabase
        .from("family_clients")
        .select(`
          id,
          client_id,
          role,
          is_primary,
          notes,
          created_at
        `)
        .eq("family_id", id);

      if (clientsError) {
        console.error("❌ Erreur chargement clients:", clientsError);
      }

      // 3. Charger les profils de chaque client
      if (clientsData && clientsData.length > 0) {
        const clientIds = clientsData.map(c => c.client_id);
        
        const { data: profilesData, error: profilesError } = await supabase
          .from("profiles")
          .select("*")
          .in("id", clientIds);

        if (profilesError) {
          console.error("❌ Erreur chargement profils:", profilesError);
        }

        // Fusionner les données clients + profils
        clientsWithProfiles = clientsData.map(client => {
          const profile = profilesData?.find(p => p.id === client.client_id);
          return {
            ...client,
            profile: profile || null
          };
        });

        setFamilyClients(clientsWithProfiles);

        // 4. Charger les collections de TOUS les clients
        let collectionsError;
        ({ data: collectionsData, error: collectionsError } = await supabase
          .from("collections")
          .select("*")
          .in("client_id", clientIds));
        if (!collectionsError && collectionsData) {
          setCollections(collectionsData);

          // 5. Charger les artworks de ces collections
          const collectionIds = collectionsData.map(c => c.id);
          if (collectionIds.length > 0) {
            const { data: artworksRes } = await supabase
              .from("artworks_secure")
              .select("*")
              .in("collection_id", collectionIds);
            artworksData = artworksRes || [];
            setArtworks(artworksData);
          }
        }



        // 6. Charger les bénéficiaires de tous les clients
        let beneficiariesError;
        ({ data: beneficiariesData, error: beneficiariesError } = await supabase
          .from("beneficiaries")
          .select("*")
          .in("client_id", clientIds));

        if (!beneficiariesError && beneficiariesData) {
          setBeneficiaries(beneficiariesData);
        }

      } else {
        setFamilyClients([]);
      }

      // Auto-export PDF si ?rapport= dans l'URL — données locales fraîches
      triggerAutoExport({
        foProfileLocal,
        familyLocal: familyData,
        familyClientsLocal: clientsWithProfiles || [],
        artworksLocal: artworksData || [],
        collectionsLocal: collectionsData || [],
        beneficiariesLocal: beneficiariesData || [],
      });

    } catch (err) {
      console.error("❌ Erreur chargement famille:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, c.famNotFound]);

  useEffect(() => {
    loadFamilyData();
  }, [loadFamilyData]);

  // Déclenche le PDF auto si ?rapport= dans l'URL
  // Appelé depuis loadFamilyData avec les données locales fraîches (pas le state)
  const triggerAutoExport = (localData) => {
    const rapportAuto = new URLSearchParams(window.location.search).get("rapport");
    if (!rapportAuto) return;
    const { foProfileLocal, familyLocal, familyClientsLocal, artworksLocal, collectionsLocal, beneficiariesLocal } = localData;
    // Nettoyer l'URL immédiatement pour éviter re-trigger
    const url = new URL(window.location.href);
    url.searchParams.delete("rapport");
    window.history.replaceState({}, "", url.toString());
    // Générer le PDF avec les données fraîches
    generateFamilyPdf({
      foProfile: foProfileLocal,
      family: familyLocal,
      familyClients: familyClientsLocal,
      artworks: artworksLocal,
      collections: collectionsLocal,
      beneficiaries: beneficiariesLocal,
      reportType: rapportAuto,
    });
  };

  // Calculer les statistiques
  const stats = useMemo(() => {
    const totalValue = artworks.reduce((sum, art) => {
      const value = parseFloat(art.estimated_value || art.value || 0);
      return sum + value;
    }, 0);

    return {
      totalClients: familyClients.length,
      totalBeneficiaries: beneficiaries.length,
      totalCollections: collections.length,
      totalArtworks: artworks.length,
      totalValue
    };
  }, [familyClients, beneficiaries, collections, artworks]);

  // Ajouter un client
  const handleAddClient = async () => {
    if (!newClientEmail.trim()) {
      alert(c.alertEmail);
      return;
    }

    setAddingClient(true);
    try {
      // 1. Trouver le profil par email
      const { data: profileId, error: lookupError } = await supabase.rpc(
        "get_profile_id_by_email",
        { p_email: newClientEmail.trim() }
      );

      if (lookupError) throw lookupError;
      if (!profileId) throw new Error(c.noProfile);

      // 2. Vérifier si déjà client de cette famille
      const existingClient = familyClients.find(c => c.client_id === profileId);
      if (existingClient) {
        alert(c.alertAlready);
        return;
      }

      // 3. Ajouter le client
      const { error: insertError } = await supabase
        .from("family_clients")
        .insert({
          family_id: id,
          client_id: profileId,
          role: newClientRole,
          is_primary: familyClients.length === 0 // Premier client = primary
        });

      if (insertError) throw insertError;

      // 4. Recharger les données
      await loadFamilyData();
      
      // 5. Fermer le modal
      setShowAddClient(false);
      setNewClientEmail("");
      setNewClientRole("member");

    } catch (err) {
      console.error("❌ Erreur ajout client:", err);
      alert(c.errorPrefix + err.message);
    } finally {
      setAddingClient(false);
    }
  };

  // Retirer un client
  const handleRemoveClient = async (clientLinkId, clientName) => {
    if (!window.confirm(`Retirer ${clientName} de cette famille ?\n\nSes collections resteront intactes.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from("family_clients")
        .delete()
        .eq("id", clientLinkId);

      if (error) throw error;

      await loadFamilyData();
    } catch (err) {
      console.error("❌ Erreur suppression client:", err);
      alert(c.errorPrefix + err.message);
    }
  };

  // Définir comme contact principal
  const handleSetPrimary = async (clientLinkId) => {
    try {
      // 1. Retirer le flag primary de tous les autres
      await supabase
        .from("family_clients")
        .update({ is_primary: false })
        .eq("family_id", id);

      // 2. Définir ce client comme primary
      const { error } = await supabase
        .from("family_clients")
        .update({ is_primary: true })
        .eq("id", clientLinkId);

      if (error) throw error;

      await loadFamilyData();
    } catch (err) {
      console.error("❌ Erreur définition primary:", err);
      alert(c.errorPrefix + err.message);
    }
  };

  // Supprimer la famille
  const handleDeleteFamily = async () => {
    if (!window.confirm(
      c.deleteConfirm(family.label)
    )) {
      return;
    }

    try {
      const { error: deleteError } = await supabase
        .from("families")
        .delete()
        .eq("id", id);

      if (deleteError) throw deleteError;

      navigate("/family-office/manage");
    } catch (err) {
      console.error("❌ Erreur suppression:", err);
      alert(c.errorDelete + err.message);
    }
  };

  // Export PDF — rapport blanc avec branding Family Office
  // Défini ici (FamilyDetail) pour accéder à activeTab, foProfile, beneficiaries
  // typeOverride : permet d'appeler depuis le header avec un type précis
  const exportPdf = (typeOverride) => {
    const reportType = typeOverride || (
      activeTab === "philanthropy" ? "philanthropy"
      : activeTab === "beneficiaries" ? "succession"
      : "overview"
    );

    generateFamilyPdf({
      foProfile,
      family,
      familyClients,
      artworks,
      collections,
      beneficiaries,
      reportType,
    });
  };

const handleSaveFiscalRegime = async (regime) => {
  try {
    const { error } = await supabase
      .from("families")
      .update({ fiscal_regime: regime })
      .eq("id", id);

    if (error) throw error;

    setFiscalRegime(regime);
    alert(c.alertSavedFiscal);
  } catch (error) {
    console.error("Erreur sauvegarde régime:", error);
    alert(c.alertSaveErr);
  }
};
  if (loading) {

    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-2 border-[#E8E8E8] border-t-[#1A1A1A] 
                         rounded-full animate-spin" />
          <p className="mt-4 text-sm text-[#999]">{c.loadingFam}</p>
        </div>
      </div>
    );
  }

  if (error || !family) {


    return (
      <div className="min-h-screen bg-[#FAFAF8] p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded p-6 flex items-start gap-3">
            <AlertCircle className="text-red-600 shrink-0" size={20} />
            <div>
              <p className="text-sm text-red-800 font-medium">
                {error || c.famNotFound}
              </p>
              <button
                onClick={() => navigate("/family-office/dashboard")}
                className="mt-3 text-sm text-red-600 hover:text-red-800 underline"
              >
                {c.backDashArrow}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Header */}
      <div className="border-b border-[#E8E8E8] bg-white">
        <div className="max-w-7xl mx-auto px-8 py-6">
          <button
            onClick={() => navigate("/family-office/dashboard")}
            className="flex items-center gap-2 text-sm text-[#666] hover:text-[#1A1A1A] 
                     transition-colors mb-4"
          >
            <ChevronLeft size={16} />
            {c.backDash}
          </button>

          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-light text-[#1A1A1A]">{family.label}</h1>
                <span
                  className={`px-3 py-1 rounded text-sm font-light
                  ${family.status === "active"
                    ? "bg-emerald-50 text-emerald-700"
                    : "bg-amber-50 text-amber-700"
                  }`}
                >
                  {family.status}
                </span>
              </div>
              
              <div className="flex items-center gap-6 text-sm text-[#666]">
                <span className="flex items-center gap-1">
                  <Users size={14} />
                  {c.nClients(stats.totalClients)}
                </span>
                <span className="flex items-center gap-1">
                  <Heart size={14} />
                  {c.nBenef(stats.totalBeneficiaries)}
                </span>
                {family.created_at && (
                  <span>
                    {c.createdOn} {new Date(family.created_at).toLocaleDateString(locale, {
                      day: "numeric",
                      month: "long",
                      year: "numeric"
                    })}
                  </span>
                )}
              </div>

              {family.notes && (
                <p className="mt-3 text-sm text-[#666] italic">{family.notes}</p>
              )}
            </div>

            <div className="flex items-center gap-2">
              {/* Rapport PDF — dropdown des 3 types */}
              <div className="relative group">
                <button
                  className="flex items-center gap-2 px-4 py-2 border border-[#C9A961] text-[#C9A961] rounded text-sm hover:bg-[#C9A961] hover:text-white transition-all"
                >
                  <Download size={16} />
                  {c.genReport}
                </button>
                <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-[#E8E8E8] rounded-lg shadow-lg z-10 hidden group-hover:block">
                  {[
                    { type: "overview",     label: c.repOverview },
                    { type: "succession",   label: c.repSucc },
                    { type: "philanthropy", label: c.repPhil },
                  ].map(({ type, label }) => (
                    <button
                      key={type}
                      onClick={() => exportPdf(type)}
                      className="w-full text-left px-4 py-2.5 text-sm text-[#1A1A1A] hover:bg-[#F9F7F4] first:rounded-t-lg last:rounded-b-lg transition-colors"
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={() => setShowAddClient(true)}
                className="flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] text-white rounded
                         text-sm hover:bg-[#333] transition-all"
              >
                <UserPlus size={16} />
                {c.addClient}
              </button>
              
              <button
                className="p-2 border border-[#E8E8E8] rounded hover:bg-[#F5F5F5] transition-all"
                title={c.titleEdit}
              >
                <Edit size={16} />
              </button>
              
              <button
                onClick={handleDeleteFamily}
                className="p-2 border border-[#E8E8E8] rounded hover:bg-red-50 
                         hover:text-red-600 hover:border-red-200 transition-all"
                title={c.titleDelete}
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Statistiques */}
      <div className="max-w-7xl mx-auto px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded border border-[#E8E8E8] p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users className="text-blue-600" size={20} />
              <h3 className="text-sm text-[#999]">Clients</h3>
            </div>
            <p className="text-3xl font-light text-[#1A1A1A]">{stats.totalClients}</p>
          </div>

          <div className="bg-white rounded border border-[#E8E8E8] p-6">
            <div className="flex items-center gap-3 mb-2">
              <Package className="text-amber-600" size={20} />
              <h3 className="text-sm text-[#999]">Collections</h3>
            </div>
            <p className="text-3xl font-light text-[#1A1A1A]">{stats.totalCollections}</p>
            <p className="text-xs text-[#999] mt-1">{c.nWorks(stats.totalArtworks)}</p>
          </div>

          <div className="bg-white rounded border border-[#E8E8E8] p-6">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="text-emerald-600" size={20} />
              <h3 className="text-sm text-[#999]">{c.totalValue}</h3>
            </div>
            <p className="text-3xl font-light text-[#1A1A1A]">
              {new Intl.NumberFormat(locale, {
                style: "currency",
                currency: "EUR",
                notation: "compact"
              }).format(stats.totalValue)}
            </p>
            <ValuationCaption />
          </div>

          <div className="bg-white rounded border border-[#E8E8E8] p-6">
            <div className="flex items-center gap-3 mb-2">
              <Heart className="text-rose-600" size={20} />
              <h3 className="text-sm text-[#999]">{c.beneficiaries}</h3>
            </div>
            <p className="text-3xl font-light text-[#1A1A1A]">{stats.totalBeneficiaries}</p>
          </div>
        </div>

        {/* Onglets */}
        <div className="bg-white rounded border border-[#E8E8E8] overflow-hidden">
          <div className="border-b border-[#E8E8E8] px-8 py-4 flex gap-6">
            {[
              { id: "overview", label: c.overview, icon: FileText },
              { id: "clients", label: `Clients (${familyClients.length})`, icon: Users },
              { id: "collections", label: `Collections (${collections.length})`, icon: Package },
              { id: "beneficiaries", label: `${c.beneficiaries} (${beneficiaries.length})`, icon: Heart },
	      { id: "philanthropy", label: c.tabPhil, icon: Landmark },
              { id: "fiscal", label: c.tabFiscal, icon: Banknote } 
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-light rounded transition-all
                  ${activeTab === tab.id
                    ? "bg-[#1A1A1A] text-white"
                    : "text-[#666] hover:bg-[#F5F5F5]"
                  }`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-8">
            {activeTab === "overview" && <OverviewTab family={family} stats={stats} />}
            
            {activeTab === "clients" && (
              <ClientsTab 
                clients={familyClients}
                collections={collections}
                artworks={artworks}
                onRemove={handleRemoveClient}
                onSetPrimary={handleSetPrimary}
              />
            )}
            
            {activeTab === "collections" && (
              <CollectionsTab 
                collections={collections}
                artworks={artworks}
                familyClients={familyClients}
              />
            )}
            
            {activeTab === "beneficiaries" && (
              <BeneficiariesTab 
                beneficiaries={beneficiaries}
                familyClients={familyClients}
              />
            )}
            {activeTab === "philanthropy" && (
              <PhilanthropyTab
                family={family}
                familyClients={familyClients}
                collections={collections}
                artworks={artworks}
                onExportPdf={exportPdf}
              />
            )}
{activeTab === "fiscal" && (
  <FiscalTab 
    familyId={id}
    currentRegime={fiscalRegime}
    onSave={handleSaveFiscalRegime}
  />
)}
          </div>
        </div>
      </div>

      {/* Modal ajout client */}
      {showAddClient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-light text-[#1A1A1A]">{c.addClient}</h3>
              <button
                onClick={() => setShowAddClient(false)}
                className="p-2 hover:bg-[#F5F5F5] rounded transition-all"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-[#666] mb-2">
                  {c.emailClient}
                </label>
                <input
                  type="email"
                  value={newClientEmail}
                  onChange={(e) => setNewClientEmail(e.target.value)}
                  placeholder="client@example.com"
                  className="w-full border border-[#E8E8E8] rounded px-4 py-3 text-sm
                           focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]"
                  disabled={addingClient}
                />
              </div>

              <div>
                <label className="block text-sm text-[#666] mb-2">
                  {c.role}
                </label>
                <select
                  value={newClientRole}
                  onChange={(e) => setNewClientRole(e.target.value)}
                  className="w-full border border-[#E8E8E8] rounded px-4 py-3 text-sm
                           focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]"
                  disabled={addingClient}
                >
                  <option value="owner">{c.ownerMain}</option>
                  <option value="member">{c.familyMember}</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowAddClient(false)}
                  className="flex-1 px-4 py-3 border border-[#E8E8E8] rounded text-sm
                           hover:bg-[#F5F5F5] transition-all"
                  disabled={addingClient}
                >
                  {c.cancel}
                </button>
                <button
                  onClick={handleAddClient}
                  className="flex-1 px-4 py-3 bg-[#1A1A1A] text-white rounded text-sm
                           hover:bg-[#333] transition-all disabled:opacity-50"
                  disabled={addingClient || !newClientEmail.trim()}
                >
                  {addingClient ? c.adding : c.add}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Onglet Vue d'ensemble
function OverviewTab({ family, stats }) {
  const { c, locale } = useCopy();
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-light text-[#1A1A1A]">{c.overview}</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="border border-[#E8E8E8] rounded p-6">
          <h4 className="text-sm text-[#999] mb-4">{c.familyStructure}</h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#666]">{c.memberClients}</span>
              <span className="text-lg font-light">{stats.totalClients}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#666]">{c.beneficiaries}</span>
              <span className="text-lg font-light">{stats.totalBeneficiaries}</span>
            </div>
          </div>
        </div>

        <div className="border border-[#E8E8E8] rounded p-6">
          <h4 className="text-sm text-[#999] mb-4">{c.artHoldings}</h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#666]">Collections</span>
              <span className="text-lg font-light">{stats.totalCollections}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#666]">{c.works}</span>
              <span className="text-lg font-light">{stats.totalArtworks}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#666]">{c.value}</span>
              <span className="text-lg font-light">
                {new Intl.NumberFormat(locale, {
                  style: "currency",
                  currency: "EUR",
                  notation: "compact"
                }).format(stats.totalValue)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mt-6">
        <div className="border border-[#E8E8E8] rounded p-4">
          <div className="flex items-center gap-2 text-[#666] mb-2">
            <Shield size={16} />
            <span className="text-sm">{c.insurance}</span>
          </div>
          <p className="text-sm text-[#999]">{c.notConfigured}</p>
        </div>

        <div className="border border-[#E8E8E8] rounded p-4">
          <div className="flex items-center gap-2 text-[#666] mb-2">
            <Heart size={16} />
            <span className="text-sm">{c.succPlan}</span>
          </div>
          <p className="text-sm text-[#999]">
            {stats.totalBeneficiaries > 0 ? c.nBenefParen(stats.totalBeneficiaries) : c.toDefine}
          </p>
        </div>
      </div>
    </div>
  );
}

// Onglet Clients
function ClientsTab({ clients, collections, artworks, onRemove, onSetPrimary }) {
  const { c, locale } = useCopy();
  const clientsWithStats = useMemo(() => {
    return clients.map(client => {
      const clientCollections = collections.filter(c => c.client_id === client.client_id);
      const clientArtworks = artworks.filter(a => 
        clientCollections.some(c => c.id === a.collection_id)
      );
      const totalValue = clientArtworks.reduce((sum, art) => 
        sum + parseFloat(art.estimated_value || art.value || 0), 0
      );

      return {
        ...client,
        collectionsCount: clientCollections.length,
        artworksCount: clientArtworks.length,
        totalValue
      };
    });
  }, [clients, collections, artworks]);

  if (clients.length === 0) {
    return (
      <div className="text-center py-12">
        <Users className="mx-auto mb-4 text-[#CCC]" size={48} strokeWidth={1} />
        <p className="text-sm text-[#999]">{c.noClients}</p>
      </div>
    );
  }


  return (
    <div>
      <h3 className="text-lg font-light text-[#1A1A1A] mb-6">
        {c.memberClients} ({clients.length})
      </h3>
      
      <div className="space-y-4">
        {clientsWithStats.map((client) => {
          const clientName = client.profile
            ? (client.profile.company || 
               [client.profile.first_name, client.profile.last_name].filter(Boolean).join(" ") ||
               c.noName)
            : c.unknownProfile;

          return (
            <div
              key={client.id}
              className="border border-[#E8E8E8] rounded p-6 hover:border-[#1A1A1A] transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="text-lg font-light text-[#1A1A1A]">{clientName}</h4>
                    {client.is_primary && (
                      <Crown className="text-amber-500" size={16} title={c.primaryContact} />
                    )}
                    <span className="text-xs text-[#999] px-2 py-1 bg-[#F5F5F5] rounded">
                      {client.role === 'owner' ? c.roleOwner : c.roleMember}
                    </span>
                  </div>
                  
                  {client.profile?.email && (
                    <div className="flex items-center gap-2 text-sm text-[#666] mb-3">
                      <Mail size={14} />
                      {client.profile.email}
                    </div>
                  )}

                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-[#999] mb-1">Collections</p>
                      <p className="font-light text-[#1A1A1A]">{client.collectionsCount}</p>
                    </div>
                    <div>
                      <p className="text-[#999] mb-1">{c.works}</p>
                      <p className="font-light text-[#1A1A1A]">{client.artworksCount}</p>
                    </div>
                    <div>
                      <p className="text-[#999] mb-1">{c.value}</p>
                      <p className="font-light text-[#1A1A1A]">
                        {new Intl.NumberFormat(locale, {
                          style: "currency",
                          currency: "EUR",
                          notation: "compact"
                        }).format(client.totalValue)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {!client.is_primary && (
                    <button
                      onClick={() => onSetPrimary(client.id)}
                      className="p-2 border border-[#E8E8E8] rounded hover:bg-amber-50 
                               hover:border-amber-200 transition-all"
                      title={c.setPrimary}
                    >
                      <Crown size={16} />
                    </button>
                  )}
                  
                  <button
                    onClick={() => onRemove(client.id, clientName)}
                    className="p-2 border border-[#E8E8E8] rounded hover:bg-red-50 
                             hover:text-red-600 hover:border-red-200 transition-all"
                    title={c.removeFromFam}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {client.notes && (
                <div className="pt-4 border-t border-[#E8E8E8]">
                  <p className="text-sm text-[#666] italic">{client.notes}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Onglet Collections
function CollectionsTab({ collections, artworks, familyClients }) {
  const { c, locale } = useCopy();
  const collectionsWithDetails = useMemo(() => {
    return collections.map(collection => {
      const client = familyClients.find(c => c.client_id === collection.client_id);
      const collectionArtworks = artworks.filter(a => a.collection_id === collection.id);
      const totalValue = collectionArtworks.reduce((sum, art) => 
        sum + parseFloat(art.estimated_value || art.value || 0), 0
      );

      return {
        ...collection,
        client,
        artworkCount: collectionArtworks.length,
        totalValue
      };
    });
  }, [collections, artworks, familyClients]);

  if (collections.length === 0) {
    return (
      <div className="text-center py-12">
        <Package className="mx-auto mb-4 text-[#CCC]" size={48} strokeWidth={1} />
        <p className="text-sm text-[#999]">{c.noCollections}</p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-lg font-light text-[#1A1A1A] mb-6">
        Collections ({collections.length})
      </h3>
      
      <div className="space-y-3">
        {collectionsWithDetails.map((collection) => {
          const clientName = collection.client?.profile
            ? (collection.client.profile.company || 
               [collection.client.profile.first_name, collection.client.profile.last_name]
                 .filter(Boolean).join(" "))
            : c.unknownClient;

          return (
            <div
              key={collection.id}
              className="border border-[#E8E8E8] rounded p-6 hover:border-[#1A1A1A] transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-light text-[#1A1A1A] text-lg mb-2">{collection.title || collection.name}</h4>
                  
                  <div className="flex items-center gap-4 text-sm text-[#666] mb-3">
                    <span className="flex items-center gap-1">
                      <Briefcase size={14} />
                      {clientName}
                    </span>
                    <span className="flex items-center gap-1">
                      <FileText size={14} />
                      {c.nArtworks(collection.artworkCount)}
                    </span>
                  </div>

                  {collection.description && (
                    <p className="text-sm text-[#666] mb-3">{collection.description}</p>
                  )}
                </div>

                <div className="text-right">
                  <p className="text-sm text-[#999] mb-1">{c.value}</p>
                  <p className="text-lg font-light text-[#1A1A1A]">
                    {new Intl.NumberFormat(locale, {
                      style: "currency",
                      currency: "EUR",
                      notation: "compact"
                    }).format(collection.totalValue)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Onglet Bénéficiaires
function BeneficiariesTab({ beneficiaries, familyClients }) {
  const { c } = useCopy();
  const beneficiariesByClient = useMemo(() => {
    return beneficiaries.map(beneficiary => {
      const client = familyClients.find(c => c.client_id === beneficiary.client_id);
      return {
        ...beneficiary,
        client
      };
    });
  }, [beneficiaries, familyClients]);

  if (beneficiaries.length === 0) {
    return (
      <div className="text-center py-12">
        <Heart className="mx-auto mb-4 text-[#CCC]" size={48} strokeWidth={1} />
        <p className="text-sm text-[#999]">{c.noBenef}</p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-lg font-light text-[#1A1A1A] mb-6">
        {c.beneficiaries} ({beneficiaries.length})
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {beneficiariesByClient.map((beneficiary) => {
          const clientName = beneficiary.client?.profile
            ? (beneficiary.client.profile.company || 
               [beneficiary.client.profile.first_name, beneficiary.client.profile.last_name]
                 .filter(Boolean).join(" "))
            : c.unknownClient;

          return (
            <div
              key={beneficiary.id}
              className="border border-[#E8E8E8] rounded p-5 hover:border-[#1A1A1A] transition-all"
            >
              <div className="flex items-start gap-4">
                {beneficiary.imageUrl ? (
                  <img 
                    src={beneficiary.imageUrl} 
                    alt={beneficiary.name}
                    className="w-12 h-12 rounded-full object-cover bg-[#F5F5F5]"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[#F5F5F5] flex items-center justify-center">
                    <Heart size={20} className="text-[#999]" />
                  </div>
                )}
                
                <div className="flex-1">
                  <h4 className="font-light text-[#1A1A1A] mb-1">{beneficiary.name}</h4>
                  <p className="text-xs text-[#999] mb-2">{beneficiary.relation}</p>
                  
                  <div className="flex items-center gap-2 text-xs text-[#666]">
                    <Briefcase size={12} />
                    <span>{c.from} {clientName}</span>
                  </div>
                  
                  {beneficiary.email && (
                    <div className="flex items-center gap-2 text-xs text-[#666] mt-1">
                      <Mail size={12} />
                      <span>{beneficiary.email}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
// Onglet Mécénat & philanthropie — basé sur le client principal de la famille

function PhilanthropyTab({ family, familyClients, collections, artworks, onExportPdf }) {
  const { c, locale } = useCopy();
  const [email, setEmail] = React.useState("—");
  const [note, setNote] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  // 1) Sélection du client de référence: primary sinon premier
  const primary = React.useMemo(() => {
    if (!familyClients || familyClients.length === 0) return null;
    const p = familyClients.find((c) => c.is_primary) || familyClients[0];
    return p?.profile ? p : null;
  }, [familyClients]);

  const fullName = React.useMemo(() => {
    if (!primary?.profile) return "—";
    const fn = (primary.profile.first_name || "").trim();
    const ln = (primary.profile.last_name || "").trim();
    const joined = [fn, ln].filter(Boolean).join(" ");
    return joined || "—";
  }, [primary]);


  // 🧮 Synthèse "œuvres exceptionnelles / usufruit / nue-propriété"
  const summary = useMemo(() => {
    const exceptional = artworks.filter(a => a.is_exceptional);
    const usufructed = artworks.filter(a => a.ownership_status === 'usufruct');
    const bare = artworks.filter(a => a.ownership_status === 'bare_ownership');
  
    const totalExceptional = exceptional.reduce(
      (sum, a) => sum + (parseFloat(a.current_estimated_value) || parseFloat(a.valuation_estimated_value) || parseFloat(a.estimated_value) || parseFloat(a.value) || 0), 0
    );
    const totalUsufruct = usufructed.reduce(
      (sum, a) => sum + (parseFloat(a.current_estimated_value) || parseFloat(a.valuation_estimated_value) || parseFloat(a.estimated_value) || parseFloat(a.value) || 0), 0
    );
  
    return {
      exceptionalCount: exceptional.length,
      exceptionalValue: totalExceptional,
      usufructCount: usufructed.length,
      usufructValue: totalUsufruct,
      bareCount: bare.length
    };
  }, [artworks]);

  const country = primary?.profile?.country || null;
  const regime = primary?.profile?.fiscal_regime || "NONE";
  const isFR =
    (country || "").toUpperCase() === "FR" ||
    (regime || "").toUpperCase().startsWith("FR_");

  // 2) Email depuis auth.users via RPC (email n’est pas dans profiles)
  React.useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        if (!primary?.client_id) return;
        const { data, error } = await supabase.rpc("get_user_email", { uid: primary.client_id });
        if (!ignore) setEmail(error ? "—" : (data || "—"));
      } catch {
        if (!ignore) setEmail("—");
      }
    })();
    return () => { ignore = true; };
  }, [primary?.client_id]);

  // 3) Leviers et éligibilités selon FR / régime
  const levers = React.useMemo(() => {
    return [
      {
        key: "loan",
        title: c.lev1,
        icon: <Building2 size={18} />,
        enabled: true,
        details: [
          c.lev1a,
          isFR ? c.lev1b : c.lev1c,
          c.lev1d
        ]
      },
      {
        key: "dation",
        title: c.lev2,
        icon: <Landmark size={18} />,
        enabled: isFR,
        details: [
          c.lev2a,
          c.lev2b,
          c.lev2c
        ]
      },
      {
        key: "foundation",
        title: c.lev3,
        icon: <Banknote size={18} />,
        enabled: true,
        details: [
          c.lev3a,
          c.lev3b,
          c.lev3c
        ]
      },
      {
        key: "usufruct",
        title: c.lev4,
        icon: <HandHeart size={18} />,
        enabled: isFR,
        details: [
          c.lev4a,
          c.lev4b,
          c.lev4c
        ]
      }
    ];
  }, [isFR, c]);

  // 4) Estimation de gain fiscal (indicative & pédagogique)
  const estimatedTaxBenefit = React.useMemo(() => {
    if (!isFR) return 0;
    if (regime === "FR_PLUS_VALUE") return 15000;
    if (regime === "FR_FORFAIT_6_5") return 5000;
    return 3000;
  }, [isFR, regime]);

  // 5) Enregistrement (facultatif) — table `philanthropy_strategies`
  const savePlan = async () => {
    if (!primary?.client_id) {
      alert(c.alertNoRef);
      return;
    }
    setSaving(true);
    try {
      const recommendation = {
        family_id: family.id,
        client_id: primary.client_id,
        country,
        regime,
        levers: levers.map((l) => ({ key: l.key, enabled: l.enabled })),
        note
      };

      const { error } = await supabase.from("philanthropy_strategies").insert({
   	family_id: family.id,                // 🔹 lien famille
   	profile_id: primary.client_id,       // 🔹 lien client référent
   	country: country || "FR",            // sécurisation du champ
   	fiscal_regime: regime,
   	recommendation,
   	potential_tax_saving: estimatedTaxBenefit,
   	created_at: new Date().toISOString() // cohérent avec table Supabase
      });

      if (error) {
        console.warn("philanthropy_strategies insert skipped:", error.message);
        alert(c.alertPlanNoHist);
      } else {
    // ✅ Marquer le profil comme philanthropy actif
    try {
      const { error: updErr } = await supabase
        .from("profiles")
        .update({
          philanthropy_active: true,
          // si tu as une colonne timestamp dédiée, dé-commente :
          // philanthropy_activated_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq("id", primary.client_id);
      if (updErr) {
        console.warn("profiles.philanthropy_active update skipped:", updErr.message);
      }
    } catch (e) {
      console.warn("profiles update exception:", e);
    }
        alert(c.alertPlanHist);
      }
	    } catch (e) {
      console.warn("savePlan:", e);
      alert(c.alertPlanNoHist2);
    } finally {
      setSaving(false);
    }
  };


  const prettyRegime = (r) => {
    switch (r) {
      case "FR_FORFAIT_6_5": return c.pr1;
      case "FR_PLUS_VALUE":  return c.pr2;
      case "UK_CGT_SIMPLE_20": return c.pr3;
      case "DE_PRIVATE_1Y_RULE": return c.pr4;
      default: return c.pr5;
    }
  };

  if (!primary) {
    return (
      <div className="text-center py-12">
        <Landmark className="mx-auto mb-4 text-[#CCC]" size={48} strokeWidth={1} />
        <p className="text-sm text-[#999]">{c.philNoClient}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-light text-[#1A1A1A]">{c.philTitle} — {family.label}</h3>
          <p className="text-sm text-[#666]">
            {c.refClient} <span className="font-medium">{fullName}</span> — {email}
          </p>
          <p className="text-sm text-[#666]">
            {c.countryLbl} <span className="font-medium">{country || "—"}</span> · {c.regimeLbl} <span className="font-medium">{prettyRegime(regime)}</span>
          </p>
        </div>
	<div className="bg-white border rounded p-4 mb-6">
	  <h4 className="font-medium mb-3">{c.philSynth}</h4>
	  <ul className="text-sm text-gray-700 space-y-1">
	    <li>{c.exceptionalWorks} {summary.exceptionalCount} — {summary.exceptionalValue.toLocaleString(locale)} €</li>
	    <li>{c.usufructWorks} {summary.usufructCount} — {summary.usufructValue.toLocaleString(locale)} €</li>
	    <li>{c.bareWorks} {summary.bareCount}</li>
	  </ul>
	</div>

        <button
          onClick={onExportPdf}
          className="inline-flex items-center gap-2 px-3 py-2 border rounded hover:bg-gray-50"
          title={c.exportPdfTitle}
        >
          <Download size={16} /> {c.exportPdf}
        </button>
      </div>

      <div id="fam-phila-plan" className="bg-white border border-[#E8E8E8] rounded p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-base font-medium text-[#1A1A1A]">{c.levSynth}</h4>
          <div className="text-sm text-[#666]">
            {c.taxGain}{" "}
            <span className="font-medium">
              {estimatedTaxBenefit.toLocaleString(locale)} €
            </span>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {levers.map((lev) => (
            <div
              key={lev.key}
              className={`p-4 border rounded ${lev.enabled ? "bg-gray-50" : "bg-white opacity-60"}`}
            >
              <div className="flex items-center gap-2 mb-2">
                {lev.icon}
                <div className="font-medium">{lev.title}</div>
                <span
                  className={`ml-auto text-xs px-2 py-0.5 rounded border ${
                    lev.enabled
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-gray-100 text-gray-500 border-gray-200"
                  }`}
                >
                  {lev.enabled ? c.eligible : c.notApplicable}
                </span>
              </div>
              <ul className="list-disc ml-5 text-sm text-gray-700 space-y-1">
                {lev.details.map((d, i) => <li key={i}>{d}</li>)}
              </ul>
            </div>
          ))}
        </div>

        {/* 🧩 Carte explicative — Usufruit / Nue-propriété */}
        <div className="mt-8 bg-gradient-to-r from-gray-50 via-white to-gray-50 border border-[#E8E8E8] rounded-lg p-6">
          <h4 className="text-base font-medium text-[#1A1A1A] mb-4 flex items-center gap-2">
            <HandHeart size={18} className="text-rose-600" />
            {c.dismember}
          </h4>

          <div className="grid md:grid-cols-2 gap-6 text-sm text-[#444]">
            {/* Usufruitier */}
            <div className="p-4 border border-[#E8E8E8] rounded-md bg-white">
              <h5 className="font-semibold text-[#1A1A1A] mb-2">{c.usufructuary}</h5>
              <ul className="list-disc ml-5 space-y-1">
                <li>{c.u1}</li>
                <li>{c.u2}</li>
                <li>{c.u3}</li>
              </ul>
              <p className="mt-3 text-xs text-[#777]">
                {c.uPractice}
              </p>
            </div>

            {/* Nue-propriété */}
            <div className="p-4 border border-[#E8E8E8] rounded-md bg-white">
              <h5 className="font-semibold text-[#1A1A1A] mb-2">{c.bareOwner}</h5>
              <ul className="list-disc ml-5 space-y-1">
                <li>{c.b1}</li>
                <li>{c.b2}</li>
                <li>{c.b3}</li>
              </ul>
              <p className="mt-3 text-xs text-[#777]">
                {c.bPractice}
              </p>
            </div>
          </div>

          <div className="mt-6 border-t pt-3 text-xs text-[#666]">
            <b>{c.noteB}</b> {c.noteTxt}
          </div>
        </div>
        <div className="mt-6">
          <label className="block text-sm font-medium mb-1">{c.famNote}</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-black"
            placeholder={c.famNotePh}
          />
        </div>

        <div className="mt-6 text-xs text-[#666] border-t pt-3">
          <b>{c.disclB}</b> {c.disclTxt}
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={savePlan}
          disabled={saving}
          className="px-4 py-2 bg-[#1A1A1A] text-white rounded hover:bg-[#333] disabled:opacity-50"
        >
          {saving ? c.saving : c.createPlan}
        </button>
        <button
          onClick={onExportPdf}
          className="px-4 py-2 border rounded hover:bg-gray-50"
        >
          {c.exportPdfBtn}
        </button>
      </div>
    </div>
  );
}